import type { ExecutionProgressHydration } from '../executionOperations/types';
import type { RouteOperationalIntelligence } from '../operationalIntelligence/types';
import {
  COORD_DRIVER_OVERTIME_HOURS,
  COORD_MIN_TRANSITION_HOURS,
  COORD_VEHICLE_OVER_PLAN_HOURS,
  estimateSpilloverHours,
  hoursAgo,
  hoursBetween,
  maxSeverity,
  periodEnd,
  periodStart,
  severityRank
} from './coordinationHeuristics';
import type {
  BuildCoordinationParams,
  CascadeDelayItem,
  CascadeDelayProjection,
  CoordinationFleetContext,
  CoordinationRecommendation,
  DriverOperationalImpactProjection,
  ExecutionConflictItem,
  ExecutionConflictProjection,
  ImpactSeverity,
  OperationalDependencyItem,
  OperationalDependencyProjection,
  RelatedRouteSnapshot,
  ResourceOccupancyItem,
  ResourceOccupancyProjection,
  ResourceTimelineFoundation,
  RouteOperationalCoordination,
  TimelineMarker,
  VehicleChainImpactItem,
  VehicleChainImpactProjection
} from './types';

function nextRoutesOnVehicle(
  fleet: CoordinationFleetContext,
  currentEnd: Date | null
): RelatedRouteSnapshot[] {
  if (!fleet.vehicleId) return [];
  return fleet.relatedRoutes
    .filter((r) => r.vehicleId != null && Number(r.vehicleId) === Number(fleet.vehicleId))
    .filter((r) => !r.executionComplete)
    .filter((r) => {
      const start = periodStart(r.plannedLoadingDate, null);
      if (!start || !currentEnd) return true;
      return start.getTime() >= currentEnd.getTime() - 24 * 3600000;
    })
    .sort((a, b) => {
      const ta = periodStart(a.plannedLoadingDate, null)?.getTime() ?? 0;
      const tb = periodStart(b.plannedLoadingDate, null)?.getTime() ?? 0;
      return ta - tb;
    });
}

export function buildVehicleChainImpactProjection(params: {
  fleet: CoordinationFleetContext;
  spilloverHours: number;
  intelligence: RouteOperationalIntelligence | null;
  plannedUnloadingDate?: string | null;
  hydration: ExecutionProgressHydration;
}): VehicleChainImpactProjection {
  const lastPlanned = params.hydration.stops
    .filter((s) => !s.isCompleted && s.plannedAt)
    .map((s) => s.plannedAt!)
    .pop();
  const end = periodEnd(params.plannedUnloadingDate ?? null, lastPlanned ?? null);
  const next = nextRoutesOnVehicle(params.fleet, end);

  const items: VehicleChainImpactItem[] = [];
  if (params.spilloverHours > 0) {
    for (const r of next.slice(0, 3)) {
      items.push({
        nextRouteId: r.routeId,
        nextRouteNumber: r.routeNumber,
        estimatedDelayHours: params.spilloverHours,
        reason:
          params.intelligence?.progression.stalledReason ??
          'Текущая задержка исполнения'
      });
    }
  }

  const impactedNextCount = items.length;
  let summary: string | null = null;
  if (impactedNextCount > 0 && params.spilloverHours > 0) {
    const h = Math.round(params.spilloverHours);
    summary =
      impactedNextCount === 1
        ? `Следующий маршрут может задержаться на ~${h} ч`
        : `Возможна задержка ${impactedNextCount} следующих рейсов (~${h} ч)`;
  }

  return {
    hasNextRoute: next.length > 0,
    impactedNextCount,
    items,
    summary
  };
}

export function buildDriverOperationalImpactProjection(params: {
  fleet: CoordinationFleetContext;
  spilloverHours: number;
  intelligence: RouteOperationalIntelligence | null;
  plannedUnloadingDate?: string | null;
  now: Date;
  vehicleChain: VehicleChainImpactProjection;
}): DriverOperationalImpactProjection {
  const overdue = params.plannedUnloadingDate
    ? hoursAgo(params.plannedUnloadingDate, params.now)
    : null;

  const overtimeHours =
    overdue != null && overdue >= COORD_DRIVER_OVERTIME_HOURS
      ? Math.round(overdue * 10) / 10
      : params.spilloverHours >= COORD_DRIVER_OVERTIME_HOURS
        ? params.spilloverHours
        : null;

  const inactive = params.intelligence?.progression.hoursSinceLastActivity;
  const waitingPressureLabel =
    inactive != null && inactive >= 4 ? `Ожидание ~${Math.round(inactive)} ч` : null;

  const downtimePressureLabel = params.intelligence?.risks.risks.some((r) => r.code === 'long_downtime')
    ? 'Давление простоя'
    : null;

  const nextTripAtRisk = params.vehicleChain.impactedNextCount > 0;

  let summary: string | null = null;
  if (overtimeHours != null) {
    summary = `Превышение планового времени на ~${Math.round(overtimeHours)} ч`;
  } else if (nextTripAtRisk) {
    summary = 'Следующий рейс под угрозой';
  }

  return {
    overtimeHours,
    waitingPressureLabel,
    downtimePressureLabel,
    nextTripAtRisk,
    summary
  };
}

export function buildResourceOccupancyProjection(params: {
  fleet: CoordinationFleetContext;
  spilloverHours: number;
  plannedUnloadingDate?: string | null;
  now: Date;
  hydration: ExecutionProgressHydration;
}): ResourceOccupancyProjection {
  const items: ResourceOccupancyItem[] = [];
  const seen = new Set<string>();

  const add = (item: ResourceOccupancyItem) => {
    if (seen.has(item.code)) return;
    seen.add(item.code);
    items.push(item);
  };

  const overdue = params.plannedUnloadingDate
    ? hoursAgo(params.plannedUnloadingDate, params.now)
    : null;

  if (overdue != null && overdue >= COORD_VEHICLE_OVER_PLAN_HOURS) {
    add({
      code: 'vehicle_over_plan',
      severity: overdue >= 8 ? 'HIGH' : 'MEDIUM',
      title: 'Машина занята дольше плана',
      detail: `+${Math.round(overdue)} ч к плану выгрузки`
    });
  }

  if (params.fleet.trailerVehicleId && params.spilloverHours > 0) {
    add({
      code: 'trailer_blocked',
      severity: 'MEDIUM',
      title: 'Прицеп под давлением',
      detail: 'Задержка текущего рейса'
    });
  }

  const pending = params.fleet.relatedRoutes.filter((r) => !r.executionComplete).length;
  if (pending > 0 && params.spilloverHours > 0) {
    add({
      code: 'pending_route_waiting',
      severity: pending >= 2 ? 'HIGH' : 'MEDIUM',
      title: 'Ожидают ресурс',
      detail: `${pending} связанных маршрутов`
    });
  }

  const stuckStop = params.hydration.stops.find(
    (s) => !s.isCompleted && s.completionPercent > 0 && s.completionPercent < 100
  );
  if (stuckStop) {
    add({
      code: 'stop_occupancy',
      severity: 'MEDIUM',
      title: 'Точка удерживает ресурс',
      detail: stuckStop.city ?? stuckStop.address ?? stuckStop.executionStopUid
    });
  }

  items.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));

  return {
    items,
    highestSeverity: maxSeverity(items.map((i) => i.severity)),
    vehicleOverPlanHours:
      overdue != null && overdue > 0 ? Math.round(overdue * 10) / 10 : null,
    pendingRoutesWaiting: pending
  };
}

export function buildCascadeDelayProjection(params: {
  intelligence: RouteOperationalIntelligence | null;
  hydration: ExecutionProgressHydration;
  vehicleChain: VehicleChainImpactProjection;
  resourceOccupancy: ResourceOccupancyProjection;
}): CascadeDelayProjection {
  const items: CascadeDelayItem[] = [];

  if (params.intelligence?.risks.risks.some((r) => r.code === 'overdue_unload')) {
    items.push({
      code: 'unload_to_load',
      title: 'Просроченная выгрузка',
      downstream: 'Следующая погрузка под риском'
    });
  }

  if (params.intelligence?.progression.isStalled) {
    items.push({
      code: 'stalled_dispatcher',
      title: 'Исполнение остановилось',
      downstream: 'Перегрузка диспетчеризации'
    });
  }

  if (params.hydration.progress.blockers.includes('cargo_undelivered')) {
    items.push({
      code: 'blocked_shipment',
      title: 'Груз не доставлен',
      downstream: 'Давление на следующие отгрузки'
    });
  }

  if (params.vehicleChain.impactedNextCount > 0) {
    items.push({
      code: 'chain_delay',
      title: 'Цепочка рейсов',
      downstream: params.vehicleChain.summary ?? 'Задержка следующих рейсов'
    });
  }

  if (params.resourceOccupancy.pendingRoutesWaiting >= 2) {
    items.push({
      code: 'resource_queue',
      title: 'Очередь на ресурс',
      downstream: `${params.resourceOccupancy.pendingRoutesWaiting} маршрутов ждут`
    });
  }

  const capped = items.slice(0, 4);
  return {
    items: capped,
    hasCascadeRisk: capped.length > 0,
    summary: capped.length > 0 ? capped[0].downstream : null
  };
}

export function buildExecutionConflictProjection(params: {
  fleet: CoordinationFleetContext;
  spilloverHours: number;
  plannedUnloadingDate?: string | null;
  hydration: ExecutionProgressHydration;
  now: Date;
}): ExecutionConflictProjection {
  const conflicts: ExecutionConflictItem[] = [];
  const seen = new Set<string>();

  const lastPlanned = params.hydration.stops
    .filter((s) => !s.isCompleted && s.plannedAt)
    .map((s) => s.plannedAt!)
    .pop();
  const currentEnd = periodEnd(params.plannedUnloadingDate ?? null, lastPlanned ?? null);
  const estimatedEnd =
    currentEnd && params.spilloverHours > 0
      ? new Date(currentEnd.getTime() + params.spilloverHours * 3600000)
      : currentEnd;

  for (const other of params.fleet.relatedRoutes) {
    if (other.routeId === params.fleet.currentRouteId) continue;
    const otherStart = periodStart(other.plannedLoadingDate, null);
    if (!estimatedEnd || !otherStart) continue;

    const gapHours = hoursBetween(estimatedEnd, otherStart);
    if (gapHours < COORD_MIN_TRANSITION_HOURS && gapHours >= 0) {
      const key = `timing:${other.routeId}`;
      if (!seen.has(key)) {
        seen.add(key);
        conflicts.push({
          code: 'impossible_timing',
          severity: gapHours < 1 ? 'CRITICAL' : 'HIGH',
          title: 'Нереалистичное окно перехода',
          detail: `До рейса №${other.routeNumber} ~${Math.round(gapHours)} ч`,
          conflictingRouteId: other.routeId
        });
      }
    }

    if (otherStart.getTime() < estimatedEnd.getTime() && params.fleet.vehicleId) {
      const key = `occupy:${other.routeId}`;
      if (!seen.has(key)) {
        seen.add(key);
        conflicts.push({
          code: 'double_vehicle_occupancy',
          severity: 'CRITICAL',
          title: 'Конфликт занятости машины',
          detail: `Рейс №${other.routeNumber} начинается раньше завершения текущего`,
          conflictingRouteId: other.routeId
        });
      }
    }
  }

  if (params.fleet.driverId) {
    const driverOverlaps = params.fleet.relatedRoutes.filter((a) => {
      const aStart = periodStart(a.plannedLoadingDate, null);
      const aEnd = periodEnd(a.plannedUnloadingDate, null);
      if (!aStart || !aEnd || !currentEnd) return false;
      return (
        a.routeId !== params.fleet.currentRouteId &&
        aStart.getTime() <= (estimatedEnd?.getTime() ?? currentEnd.getTime()) &&
        aEnd.getTime() >= (periodStart(params.plannedUnloadingDate ?? null, null)?.getTime() ?? 0)
      );
    });
    if (driverOverlaps.length > 0) {
      conflicts.push({
        code: 'overlapping_assignments',
        severity: 'HIGH',
        title: 'Пересечение назначений водителя',
        detail: `${driverOverlaps.length} маршрутов`
      });
    }
  }

  conflicts.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));

  return {
    conflicts: conflicts.slice(0, 5),
    hasConflict: conflicts.length > 0
  };
}

export function buildOperationalDependencyProjection(params: {
  hydration: ExecutionProgressHydration;
  intelligence: RouteOperationalIntelligence | null;
}): OperationalDependencyProjection {
  const items: OperationalDependencyItem[] = [];

  const pendingLoad = params.hydration.stops.find(
    (s) => String(s.type).toLowerCase().includes('load') && !s.isCompleted
  );
  if (pendingLoad) {
    items.push({
      code: 'pending_load',
      title: 'Ожидает погрузку',
      detail: pendingLoad.city ?? pendingLoad.address ?? 'Точка'
    });
  }

  if (params.intelligence?.risks.risks.some((r) => r.code === 'partial_unload_stagnation')) {
    items.push({
      code: 'unload_dependency',
      title: 'Зависимость выгрузки',
      detail: 'Частичная выгрузка блокирует поток'
    });
  }

  const fr = params.hydration.financeReadiness;
  if (!fr.invoiceReady || !fr.updReady) {
    items.push({
      code: 'finance_pressure',
      title: 'Финансовое давление',
      detail: fr.reasons[0] ?? 'Документы не готовы'
    });
  }

  if (params.hydration.progress.blockers.length > 0) {
    items.push({
      code: 'downstream_pressure',
      title: 'Давление на завершение',
      detail: params.hydration.progress.blockers.slice(0, 2).join(', ')
    });
  }

  return { items: items.slice(0, 4) };
}

export function buildResourceTimelineFoundation(params: {
  fleet: CoordinationFleetContext;
  vehicleChain: VehicleChainImpactProjection;
  driverImpact: DriverOperationalImpactProjection;
  spilloverHours: number;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
}): ResourceTimelineFoundation {
  const fmt = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      : null;

  const vehicleOccupancyMarkers: TimelineMarker[] = [];
  if (params.plannedLoadingDate) {
    vehicleOccupancyMarkers.push({
      key: 'veh-start',
      label: 'Занятость машины (план)',
      atLabel: fmt(params.plannedLoadingDate)
    });
  }
  if (params.plannedUnloadingDate) {
    vehicleOccupancyMarkers.push({
      key: 'veh-end',
      label: 'Плановое освобождение',
      atLabel: fmt(params.plannedUnloadingDate)
    });
  }

  const driverOccupancyMarkers: TimelineMarker[] = [];
  if (params.driverImpact.overtimeHours != null) {
    driverOccupancyMarkers.push({
      key: 'drv-over',
      label: 'Переработка (оценка)',
      atLabel: `+${Math.round(params.driverImpact.overtimeHours)} ч`
    });
  }

  const delayedChainMarkers: TimelineMarker[] = params.vehicleChain.items.map((i, idx) => ({
    key: `chain-${i.nextRouteId}-${idx}`,
    label: `Рейс №${i.nextRouteNumber}`,
    atLabel: i.estimatedDelayHours != null ? `+${Math.round(i.estimatedDelayHours)} ч` : null
  }));

  return {
    vehicleOccupancyMarkers: vehicleOccupancyMarkers.slice(0, 3),
    driverOccupancyMarkers: driverOccupancyMarkers.slice(0, 2),
    delayedChainMarkers: delayedChainMarkers.slice(0, 3)
  };
}

export function buildCoordinationRecommendations(params: {
  vehicleChain: VehicleChainImpactProjection;
  conflicts: ExecutionConflictProjection;
  cascade: CascadeDelayProjection;
  resourceOccupancy: ResourceOccupancyProjection;
  intelligence: RouteOperationalIntelligence | null;
}): CoordinationRecommendation[] {
  const items: CoordinationRecommendation[] = [];
  const push = (id: string, priority: number, title: string, reason: string) => {
    items.push({ id, priority, title, reason });
  };

  if (params.vehicleChain.hasNextRoute && params.vehicleChain.impactedNextCount > 0) {
    push('check_next_trip', 1, 'Проверить следующий рейс', params.vehicleChain.summary ?? 'Цепочка');
  }
  if (params.conflicts.hasConflict) {
    push('check_assignment', 1, 'Проверить конфликт назначения', params.conflicts.conflicts[0]?.detail ?? '');
  }
  if (params.cascade.hasCascadeRisk) {
    push('warn_dispatcher', 2, 'Предупредить диспетчера', params.cascade.summary ?? 'Каскад');
  }
  if (params.resourceOccupancy.items.some((i) => i.code === 'vehicle_over_plan')) {
    push('confirm_delay', 2, 'Подтвердить задержку', 'Машина занята дольше плана');
  }
  if (params.intelligence?.risks.risks.some((r) => r.code === 'partial_unload_stagnation')) {
    push('review_loading', 3, 'Пересмотреть загрузку', 'Частичная выгрузка влияет на цепочку');
  }

  return items.sort((a, b) => a.priority - b.priority).slice(0, 5);
}

export function buildRouteOperationalCoordination(
  params: BuildCoordinationParams
): RouteOperationalCoordination | null {
  const now = params.now ?? new Date();
  const spilloverHours = estimateSpilloverHours({
    intelligence: params.intelligence,
    plannedUnloadingDate: params.plannedUnloadingDate,
    now
  });

  const vehicleChain = buildVehicleChainImpactProjection({
    fleet: params.fleet,
    spilloverHours,
    intelligence: params.intelligence,
    plannedUnloadingDate: params.plannedUnloadingDate,
    hydration: params.hydration
  });

  const driverImpact = buildDriverOperationalImpactProjection({
    fleet: params.fleet,
    spilloverHours,
    intelligence: params.intelligence,
    plannedUnloadingDate: params.plannedUnloadingDate,
    now,
    vehicleChain
  });

  const resourceOccupancy = buildResourceOccupancyProjection({
    fleet: params.fleet,
    spilloverHours,
    plannedUnloadingDate: params.plannedUnloadingDate,
    now,
    hydration: params.hydration
  });

  const cascade = buildCascadeDelayProjection({
    intelligence: params.intelligence,
    hydration: params.hydration,
    vehicleChain,
    resourceOccupancy
  });

  const conflicts = buildExecutionConflictProjection({
    fleet: params.fleet,
    spilloverHours,
    plannedUnloadingDate: params.plannedUnloadingDate,
    hydration: params.hydration,
    now
  });

  const dependencies = buildOperationalDependencyProjection({
    hydration: params.hydration,
    intelligence: params.intelligence
  });

  const timelineFoundation = buildResourceTimelineFoundation({
    fleet: params.fleet,
    vehicleChain,
    driverImpact,
    spilloverHours,
    plannedLoadingDate: params.plannedLoadingDate,
    plannedUnloadingDate: params.plannedUnloadingDate
  });

  const recommendations = buildCoordinationRecommendations({
    vehicleChain,
    conflicts,
    cascade,
    resourceOccupancy,
    intelligence: params.intelligence
  });

  const severities: ImpactSeverity[] = [
    resourceOccupancy.highestSeverity,
    ...conflicts.conflicts.map((c) => c.severity),
    vehicleChain.impactedNextCount > 0 ? 'MEDIUM' : null,
    cascade.hasCascadeRisk ? 'MEDIUM' : null
  ].filter(Boolean) as ImpactSeverity[];

  const highestImpactSeverity = maxSeverity(severities);

  const impactParts: string[] = [];
  if (vehicleChain.summary) impactParts.push(vehicleChain.summary);
  if (driverImpact.summary) impactParts.push(driverImpact.summary);
  if (conflicts.hasConflict) impactParts.push(conflicts.conflicts[0]?.title ?? 'Конфликт');
  const impactSummary = impactParts.length > 0 ? impactParts[0] : null;

  return {
    routeId: params.hydration.routeId,
    vehicleChain,
    driverImpact,
    resourceOccupancy,
    cascade,
    conflicts,
    dependencies,
    timelineFoundation,
    recommendations,
    impactSummary,
    highestImpactSeverity
  };
}
